import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { patientService } from '@/services/patientService';
import type { Patient } from '@/services/types';

/**
 * Lazily fetches the patient record for the signed-in user from
 * `GET /patients/me` and caches it for the rest of the SPA lifetime.
 *
 * The auth payload is intentionally lean — it doesn't carry the patient code
 * (`PAT-XXXXX`) needed for patient-portal API calls. Pages that need it call
 * this composable, which fires the request once on first use and serves the
 * cached value to every subsequent caller.
 *
 * Reset on logout via `resetCurrentPatient()` (called from the auth store).
 */

const profile = ref<Patient | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
let inflight: Promise<void> | null = null;

async function loadOnce(): Promise<void> {
  if (profile.value) return;
  if (inflight) return inflight;

  isLoading.value = true;
  error.value = null;
  inflight = patientService
    .getMyProfile()
    .then((response) => {
      if (response.success && response.data) {
        profile.value = response.data;
      } else {
        // 404 = the signed-in user has no patient record (data drift).
        error.value = response.error ?? 'Failed to load patient profile';
      }
    })
    .catch((err: any) => {
      error.value = err?.response?.data?.message ?? 'Failed to load patient profile';
    })
    .finally(() => {
      isLoading.value = false;
      inflight = null;
    });

  return inflight;
}

export function useCurrentPatient(): {
  profile: Ref<Patient | null>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  patientId: ComputedRef<string>;
  fullName: ComputedRef<string>;
  refresh: () => Promise<void>;
} {
  void loadOnce();

  const patientId = computed(() => profile.value?.id ?? '');
  const fullName = computed(() => (profile.value ? `${profile.value.firstName} ${profile.value.lastName}`.trim() : ''));

  const refresh = async () => {
    profile.value = null;
    inflight = null;
    await loadOnce();
  };

  return { profile, isLoading, error, patientId, fullName, refresh };
}

/** Clear the cached patient profile — call from the auth store on logout. */
export function resetCurrentPatient(): void {
  profile.value = null;
  isLoading.value = false;
  error.value = null;
  inflight = null;
}
